import React, { useState, useMemo } from 'react';
import i18n from '@/common/agent/i18n';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/signin/datacontroller/AuthContext';
import logoSrc from '@/common/resource/logo/aace_512.png';
import IdField from '@/auth/signin/view/LoginIdFieldView';
import PasswordField from '@/auth/signin/view/LoginPasswordFieldView';
import Button from '@/common/view/button';
import { getRedirectPath } from '@/main/controller/MainController';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getLoginFormSchema } from '@/auth/signin/model/AuthFormSchema';
import { LoginErrorCode, LoginError } from '@/auth/signin/agent/SignInApiAgent';
import { getCustomError } from '@/common/agent/ApiAgent';
import { toast } from '@/common/view/Toast';


const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(600px circle at 35% 25%, rgba(59, 130, 246, 0.08), transparent 70%),
    radial-gradient(500px circle at 90% 90%, rgba(168, 85, 247, 0.07), transparent 70%),
    radial-gradient(400px circle at 70% 50%, rgba(236, 72, 153, 0.06), transparent 70%),
    white;
`;

const LoginBox = styled.div`
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
  padding: 48px 56px;
  width: 480px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.6s ease-out;
`;

const LoginFormBox = styled.div`
  margin-top: 24px;
  width: 330px;
  animation: ${slideUp} 0.5s ease-out 0.2s both;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LoginButton = styled(Button)`
  border-radius: 24px !important;
  height: 48px;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
`;

type LoginForm = {
  id: string;
  password: string;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const loginFormSchema = useMemo(() => getLoginFormSchema(), []);
  const methods = useForm<LoginForm>({
    resolver: yupResolver(loginFormSchema),
    defaultValues: {
      id: '',
      password: '',
    },
  });
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const onValid = async (data: LoginForm) => {
    setLoading(true);
    try {
      await login(data.id, data.password);
      navigate(getRedirectPath(window.location), { replace: true });
    } catch (err) {
      const loginError = getCustomError<LoginError>(err);
      if (!loginError) return;

      const { code, message } = loginError.detail;
      toast.error(message);

      if (code === LoginErrorCode.LoginIdNotFound) {
        methods.setError('id', { message, type: 'manual' });
      } else if (code === LoginErrorCode.PasswordIncorrect) {
        methods.setError('password', { message, type: 'manual' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <LoginBox>
        <img src={logoSrc} alt="logo" className="w-36 mb-5" />
        <FormProvider {...methods}>
          <LoginFormBox>
            <StyledForm onSubmit={methods.handleSubmit(onValid)}>
              <IdField />
              <PasswordField />
              <LoginButton stretch loading={loading} type="submit" skin="primary">
                {i18n.t('common.login')}
              </LoginButton>
            </StyledForm>
            <div className="flex gap-1 items-center justify-center mt-5">
              <span className="text-sm text-gray-400">{i18n.t('login.first_time_helper')}</span>
              <button
                type="button"
                onClick={() => window.ChannelIO?.('showMessenger')}
                className="text-sm font-semibold underline text-blue-600"
              >
                {i18n.t('login.signup_button')}
              </button>
            </div>
          </LoginFormBox>
        </FormProvider>
      </LoginBox>
    </Container>
  );
};

export default LoginPage;
